import type { Ticket, TicketMarket } from "@/Overtime/types/markets"
import type { Bet, BetOutcome } from "@azuro-org/sdk"
import {
  GameState,
  GraphBetStatus,
  BetStatus,
  getBetStatus,
} from "@azuro-org/toolkit"
import type { League } from "overtime-utils"
import { getLeagueSport, getLeagueLabel } from "overtime-utils"
import { getTeamImageSource, getErrorImage } from "@/Overtime/utils/images"
import { getPositionTextV2, getTitleText } from "@/Overtime/utils/marketsV2"

// Extended BetOutcome type with playerName field
type ExtendedBetOutcome = BetOutcome & {
  playerName?: string | null
}

// Helper to determine if a market is live
function isMarketLive(market) {
  const now = new Date()
  const maturity = new Date(market.maturityDate)
  return maturity < now && !market.isResolved && !market.isCancelled
}

export function mapOvertimeTicketToAzuroBet(
  ticket: Ticket
): Bet & { outcomes: ExtendedBetOutcome[] } {
  // keys used in showing buttons
  // isWin, isCancelled, isRedeemed

  const createdAt = Math.floor(ticket.timestamp / 1000)

  // Map outcomes first
  const outcomes = ticket.sportMarkets.map(
    (market, index): ExtendedBetOutcome => {
      const leagueId = market.leagueId as League
      const sport = getLeagueSport(leagueId)
      const leagueName = getLeagueLabel(leagueId)
      const homeImage =
        getTeamImageSource(market.homeTeam, leagueId) || getErrorImage(leagueId)
      const awayImage =
        getTeamImageSource(market.awayTeam, leagueId) || getErrorImage(leagueId)

      const isLive = isMarketLive(market)
      const isFinished =
        market.isGameFinished || market.isResolved || market.isCancelled
      let isWin: boolean | null = null
      let isLose: boolean | null = null
      if (isFinished && !isLive) {
        if (market.isWinning === true) {
          isWin = true
        } else if (market.isWinning === false) {
          isLose = true
        }
      }
      return {
        odds: market.odd,
        marketName: getTitleText(market) || "",
        selectionName: getPositionTextV2(market, market.position, true) || "",
        playerName:
          market.playerProps?.playerName === "Player Name"
            ? null
            : market.playerProps?.playerName || null,
        isWin,
        isLose,
        isLive: ticket.isLive,
        isCanceled: ticket.isCancelled ?? false,
        outcomeId: String(market.position ?? index),
        conditionId: market.gameId || `${ticket.id}-${index}`,
        wonOutcomeIds: Array.isArray(market.winningPositions)
          ? market.winningPositions.map(String)
          : [],
        game: {
          gameId: market.gameId || `overtime-${ticket.id}-${index}`,
          id: market.gameId || `overtime-${ticket.id}-${index}`,
          slug:
            `${market.homeTeam?.toLowerCase().replace(/\s+/g, "-")}-vs-${market.awayTeam?.toLowerCase().replace(/\s+/g, "-")}` ||
            "",
          __typename: "Game",
          title: `${market.homeTeam || "Team A"} vs ${market.awayTeam || "Team B"}`,
          startsAt: String(Math.floor(market.maturity)),
          state: market.isCancelled
            ? GameState.Stopped
            : market.isGameFinished
              ? GameState.Finished
              : market.isResolved
                ? GameState.Finished
                : ticket.isLive
                  ? GameState.Live
                  : GameState.Prematch,
          turnover: "0",
          sport: {
            name: sport,
            slug:
              String(sport).toLowerCase().replace(/\s+/g, "-") || "football",
            sportId: String(sport),
            sporthub: {
              __typename: "SportHub",
              id: String(sport),
              slug:
                String(sport).toLowerCase().replace(/\s+/g, "-") || "football",
            },
          },
          league: {
            id: String(leagueId),
            name: leagueName || "Unknown League",
            slug: leagueName?.toLowerCase().replace(/\s+/g, "-") || "",
          },
          country: {
            id: "international-tournaments",
            name: "International Tournaments",
            slug: "/international-tournaments",
          },
          participants: [
            {
              name: market.homeTeam || "Team A",
              image: homeImage,
            },
            {
              name: market.awayTeam || "Team B",
              image: awayImage,
            },
          ],
        },
      }
    }
  )

  // Azuro-style status logic for combos
  let status: GraphBetStatus
  let isWin = false
  let isLose = false
  if (outcomes.length > 1) {
    // Overtime combo bet logic
    const markets = ticket.sportMarkets
    const isOpen = markets.some(m => !m.isResolved)
    const isLost = markets.some(m => m.isResolved && m.isWinning === false)
    const isWon = markets.every(m => m.isResolved && m.isWinning === true)

    if (ticket.isCancelled) {
      status = GraphBetStatus.Canceled
      isLose = false // Not lost, just cancelled
    } else if (isOpen && ticket.isOpen) {
      status = GraphBetStatus.Accepted
    } else if (isLost) {
      status = GraphBetStatus.Resolved
      isLose = true
    } else if (isWon) {
      status = GraphBetStatus.Resolved
      isWin = true
    } else {
      status = GraphBetStatus.Resolved // fallback for edge cases
    }
  } else {
    // Single bet: use ticket fields as before
    if (ticket.isCancelled) {
      status = GraphBetStatus.Canceled
      isLose = false // Not lost, just cancelled
    } else if (ticket.isResolved || ticket.isUserTheWinner || ticket.isLost) {
      status = GraphBetStatus.Resolved
      // Single bet win/lose logic (unchanged)
      const allFinished = outcomes.every(
        o => o.isWin !== null || o.isLose !== null
      )
      const allWon = allFinished && outcomes.every(o => o.isWin === true)
      const anyLost = allFinished && outcomes.some(o => o.isLose === true)
      isWin = allWon
      isLose = anyLost
    } else {
      status = GraphBetStatus.Accepted
    }
  }

  return {
    tokenId: ticket.id,
    txHash: (ticket.id.startsWith("0x")
      ? ticket.id
      : `0x${ticket.id}`) as `0x${string}`,
    createdAt,
    amount: String(ticket.buyInAmount),
    payout: ticket.payout,
    totalOdds: ticket.totalQuote,
    isWin,
    isLose,
    isCanceled: ticket.isCancelled || false,
    status,
    possibleWin: ticket.payout,
    freebetId: ticket.isFreeBet ? "freebet" : null,
    isRedeemed: !ticket.isOpen && !ticket.isClaimable && ticket.isResolved,
    lpAddress: "0x0000000000000000000000000000000000000000",
    coreAddress: "0x0000000000000000000000000000000000000000",
    isRedeemable: ticket.isClaimable,
    actor: "0x0000000000000000000000000000000000000000",
    affiliate: "0x0000000000000000000000000000000000000000",
    redeemedTxHash: null,
    resolvedAt: null,
    isCashedOut: false,
    outcomes,
    isFreebetAmountReturnable: false,
    paymaster: null,
  }
}

// Example data object from overtime for useUserTicketsQuery func

// {
//   "id": "0xE6ab85FB53B0D0e3c0B8940080667dad36e5D274",
//   "txHash": "",
//   "timestamp": 1750913910000,
//   "collateral": "USDC",
//   "account": "0x97153e31ABfD0eA7c782B0c8a97026Ea07E98e00",
//   "buyInAmount": 3,
//   "fees": 0.06,
//   "totalQuote": 0.487804878049,
//   "payout": 6.149999999997232,
//   "numOfMarkets": 1,
//   "expiry": 1758689910000,
//   "isResolved": false,
//   "isPaused": false,
//   "isCancelled": false,
//   "isLost": false,
//   "isUserTheWinner": false,
//   "isExercisable": false,
//   "isClaimable": false,
//   "isOpen": true,
//   "finalPayout": 0,
//   "isLive": false,
//   "isSgp": false,
//   "isFreeBet": false,
//   "isSystemBet": false,
//   "sportMarkets": [
//       {
//           "gameId": "0x335f313839443538433138413335000000000000000000000000000000000000",
//           "sport": "Baseball",
//           "leagueId": 3,
//           "subLeagueId": 3,
//           "leagueName": "",
//           "typeId": 10001,
//           "type": "spread",
//           "maturity": 1750967100000,
//           "apiMaturity": 1750967100000,
//           "maturityDate": "2025-06-26T19:45:00.000Z",
//           "homeTeam": "San Francisco Giants",
//           "awayTeam": "Miami Marlins",
//           "homeScoreByPeriod": [],
//           "awayScoreByPeriod": [],
//           "status": 0,
//           "isOpen": true,
//           "isResolved": false,
//           "isCancelled": false,
//           "isWinning": false,
//           "isPaused": false,
//           "isOneSideMarket": false,
//           "line": -1,
//           "isPlayerPropsMarket": false,
//           "isOneSidePlayerPropsMarket": false,
//           "isYesNoPlayerPropsMarket": false,
//           "playerProps": {
//               "playerId": 0,
//               "playerName": "Player Name"
//           },
//           "combinedPositions": [],
//           "odds": [],
//           "proof": [],
//           "selectedCombinedPositions": [],
//           "position": 1,
//           "odd": 0.487804878049,
//           "childMarkets": [],
//           "winningPositions": [],
//           "isGameFinished": false,
//           "gameStatus": "unplayed"
//       }
//   ]
// }
